import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UpgradeType } from '@freelancer/components/upgrade-tag';
import type { UsernameBadges } from '@freelancer/components/username';
import { UsernameSize } from '@freelancer/components/username';
import type {
  Bid,
  FreelancerBidRestrictions,
  PoolModel,
} from '@freelancer/datastore/collections';
import {
  BidBuyerProjectFee,
  BidRating,
  Enterprise,
  FreelancerReputation,
  IpContract,
  Profile,
  Project,
  ProjectContractSignatures,
  ProjectViewBid,
  ProjectViewUser,
  RECRUITER_RECOMMENDED_SCORE,
  User,
} from '@freelancer/datastore/collections';
import { Feature, FeatureFlagsService } from '@freelancer/feature-flags';
import { ModalService, trackById } from '@freelancer/ui';
import {
  CalloutColor,
  CalloutPlacement,
  CalloutSize,
} from '@freelancer/ui/callout';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import type { QueryParams } from '@freelancer/ui/link';
import { LinkUnderline } from '@freelancer/ui/link';
import { ModalSize } from '@freelancer/ui/modal';
import { OnlineIndicatorSize } from '@freelancer/ui/online-indicator';
import { RibbonColor, RibbonPlacement } from '@freelancer/ui/ribbon';
import { TagColor, TagSize } from '@freelancer/ui/tag';
import { FontWeight } from '@freelancer/ui/text';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import { isDefined } from '@freelancer/utils';
import {
  BidAwardStatusApi,
  DisplayBidRelevancyLabelApi,
  ProjectStatusApi,
  ProjectTypeApi,
} from 'api-typings/projects/projects';
import { CorporateStatusApi } from 'api-typings/users/users';
import type { Observable, Subscription } from 'rxjs';
import { combineLatest, ReplaySubject } from 'rxjs';
import { filter, map, startWith, take } from 'rxjs/operators';
import { RibbonType } from './bid-ribbon.types';

@Component({
  standalone: false,
  selector: 'fl-bid-pattern-card-additional-details',
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BidPatternCardAdditionalDetailsComponent {}

@Component({
  standalone: false,
  selector: 'fl-bid-pattern-card-actions-notes',
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BidPatternCardActionsNotesComponent {}

@Component({
  standalone: false,
  selector: 'fl-bid-pattern-card-actions',
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BidPatternCardActionsComponent {}

@Component({
  standalone: false,
  selector: 'fl-bid-pattern-card-annotation',
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BidPatternCardAnnotationComponent {}

@Component({
  standalone: false,
  selector: 'fl-bid-pattern-card',
  template: `
    <ng-container>
      <div #bidCard>
        <div class="BidCardHeader desktop:mb-xxsmall">
          <div class="UserInfoContainer mb-xsmall desktop:mb-none">
            @if (!videoSrc && !showOnboardingModal) {
              <fl-link
                [link]="
                  biddersMultiProfile
                    ? '/u/' + bidder.username + '/id/' + biddersMultiProfile.id
                    : '/u/' + bidder.username
                "
                [underline]="LinkUnderline.NEVER"
                [queryParams]="userLinkQueryParams"
              >
                <ng-container [ngTemplateOutlet]="avatar"></ng-container>
              </fl-link>
            } @else {
              <ng-container [ngTemplateOutlet]="avatar"></ng-container>
            }
            <ng-template #avatar>
              <fl-user-avatar
                class="mr-small"
                flTrackingSection="BidCard"
                flTrackingLabel="{{
                  videoSrc === undefined
                    ? 'UserAvatarClick'
                    : isEmployerView
                      ? 'UserAvatarVideoClientClick'
                      : 'UserAvatarVideoFreelancerClick'
                }}"
                [disableLastActiveTimestampStatus]="true"
                [flTrackingReferenceType]="'bid_id'"
                [flTrackingReferenceId]="bid.id"
                [size]="AvatarSize.LARGE"
                [sizeDesktop]="AvatarSize.XLARGE"
                [users]="[bidder]"
                [isVideoAvatar]="videoSrc !== undefined"
                [isOnline]="isOnline"
                [onlineIndicatorSizeDesktop]="OnlineIndicatorSize.LARGE"
                [showActivityStatus]="true"
                [showOnboardingModal]="showOnboardingModal"
                [videoSrc]="videoSrc"
                (videoModalVideoPlayed)="handleVideoModalVideoPlayed()"
                (videoModalVideoEnded)="handleVideoModalVideoEnded()"
              ></fl-user-avatar>
            </ng-template>
            <div>
              <fl-user-info
                class="UserInfo desktop:mb-xxsmall"
                [usernameMarginBottomDesktop]="'xxsmall'"
                [user]="bidder"
                [userLink]="
                  biddersMultiProfile
                    ? '/u/' + bidder.username + '/id/' + biddersMultiProfile?.id
                    : '/u/' + bidder.username
                "
                [userLinkQueryParams]="userLinkQueryParams"
                [userBadges]="userBadges"
                [ratingControl]="ratingControl"
                [freelancerReputation]="reputation"
                [usernameSize]="UsernameSize.LARGE"
                [usernameSizeDesktop]="UsernameSize.XLARGE"
                [showCity]="showCity"
                [showEarningScore]="
                  (bidCardSettings$ | flAsync)?.bidCardEarnings
                "
                [showCountry]="(bidCardSettings$ | flAsync)?.bidCardCountry"
                [isDeloitteProject]="isDeloitteProject"
                [isTokenProject]="isTokenProject"
                [isBidder]="isBidder"
                [showNewFreelancerLabel]="showNewFreelancerLabel"
              >
              </fl-user-info>
              <p class="text-body-16 font-bold mobile:hide">
                {{ biddersMultiProfile?.tagline ?? bidder.tagLine }}
              </p>
            </div>
          </div>
          <div class="PriceContainer">
            @if (
              showBidRelevanceWarning &&
              bid.relevanceLabel === DisplayBidRelevancyLabelApi.C &&
              isUsersBid
            ) {
              <div class="WarningFlag">
                <fl-icon
                  class="mr-xxxsmall"
                  [color]="IconColor.LIGHT"
                  [name]="'ui-warning'"
                  [size]="IconSize.SMALL"
                  [useIconFont]="true"
                  [fillIconFont]="true"
                ></fl-icon>
                <span
                  i18n="Warning flag"
                  class="font-bold text-body-16"
                >
                  Warning
                </span>
              </div>
            }
            @if (!isTokenProject) {
              <div
                class="mb-xxsmall desktop:mb-none"
                [class.PaymentFeeRate]="showPaymentFeeRate$ | flAsync"
                [class.PriceRate]="!(showPaymentFeeRate$ | flAsync)"
              >
                @if (showBidAmountAndDescription) {
                  <p class="PriceDetails">
                    <span
                      class="PriceDetails-amount text-body-24 font-bold mr-xxsmall desktop:mr-none"
                      [matTooltip]="priceTooltip ?? ''"
                      [matTooltipPosition]="'above'"
                    >
                      {{ bid.amount | flCurrency: project.currency.code }}
                    </span>
                    <!-- LoadShift Employer bid total fees -->
                    @if (
                      (isEmployerView || isCollaboratorView) &&
                      (bidCardSettings$ | flAsync)?.bidTotalFee
                    ) {
                      @if (
                        salesTaxEnabled && bid.salesTax && bid.salesTax.taxType
                      ) {
                        <span class="PriceDetails-gstContainer">
                          <span
                            class="text-body-16"
                            matTooltip="3.3% project fee incl. GST"
                            [matTooltipPosition]="'below'"
                            i18n-matTooltip="Projectfee tool tip"
                            i18n="Including tax type amount label"
                          >
                            {{
                              totalFee
                                | flCurrency
                                  : project.currency.code
                                  : { disableCode: true }
                            }}
                            total
                          </span>
                          <fl-tag
                            [color]="TagColor.SUCCESS_LIGHT"
                            [size]="TagSize.MID"
                            [fontWeight]="FontWeight.BOLD"
                            [truncate]="false"
                          >
                            <fl-icon
                              class="GstCheckIcon"
                              [name]="'ui-check'"
                              [size]="IconSize.SMALL"
                              [useIconFont]="true"
                              [weight]="FontWeight.BOLD"
                              [color]="IconColor.SUCCESS"
                            ></fl-icon>
                            <span
                              i18n="GST included label on bid price tag"
                              class="GstText text-success"
                              >Incl. GST
                            </span>
                          </fl-tag>
                        </span>
                      }
                    } @else {
                      @if (
                        salesTaxEnabled && bid.salesTax && bid.salesTax.taxType
                      ) {
                        <span
                          class="text-body-16 mr-xxsmall desktop:mr-none"
                          i18n="Including tax type amount label"
                        >
                          Including {{ bid.salesTax.taxType }}
                        </span>
                      }
                    }
                    <span
                      class="PriceDetails-rate text-body-16 mr-xxsmall desktop:mr-none desktop:mb-xxsmall"
                      i18n="Freelancer rate per hour"
                    >
                      <!-- Hourly Project label -->
                      @if (bid.amount && isProjectHourly) {
                        per hour
                      }

                      <!-- Fixed Price Project label -->
                      <ng-container *flFeature="Feature.PROJECT_DELIVER_PERIOD">
                        @if (bid.amount && !isProjectHourly) {
                          <ng-container
                            *ngTemplateOutlet="fixedBidAmountLabel"
                          ></ng-container>
                        }
                      </ng-container>
                    </span>
                  </p>
                }
                <!-- Ribbons -->
                @if (showRibbon) {
                  @switch (ribbon) {
                    @case (RibbonType.RECOMMENDED) {
                      <div class="Ribbon">
                        @if (agent) {
                          <fl-ribbon
                            i18n="Recommended bid"
                            i18n-matTooltip="Recommended bid tooltip"
                            matTooltip="We've recommended a freelancer for you based on the experience and ratings of each freelancer on similar project types."
                            [matTooltipPosition]="'above'"
                            [matTooltipDisabled]="
                              !(isEmployerView && isDeloitteExternalBid)
                            "
                            [color]="RibbonColor.RECRUITER"
                            [placement]="RibbonPlacement.RIGHT"
                          >
                            Recommended by {{ agent.displayName }}
                          </fl-ribbon>
                        }
                        @if (!agent) {
                          <fl-ribbon
                            i18n="Recommended bid"
                            i18n-matTooltip="Recommended bid tooltip"
                            matTooltip="We've recommended a freelancer for you based on the experience and ratings of each freelancer on similar project types."
                            [matTooltipPosition]="'above'"
                            [matTooltipDisabled]="
                              !(isEmployerView && isDeloitteExternalBid)
                            "
                            [color]="RibbonColor.PRIMARY"
                            [placement]="RibbonPlacement.RIGHT"
                          >
                            Recommended
                          </fl-ribbon>
                        }
                      </div>
                    }
                    @case (RibbonType.SPONSORED) {
                      <div class="Ribbon">
                        <fl-ribbon
                          i18n="Sponsored bid"
                          [color]="RibbonColor.SECONDARY"
                          [placement]="RibbonPlacement.RIGHT"
                        >
                          Sponsored
                        </fl-ribbon>
                      </div>
                    }
                  }
                }
              </div>
            }

            <ng-template #fixedBidAmountLabel>
              <ng-container i18n="Fixed project bid amount label">
                in {{ bid.period }}
                @if (bid.period === 1) {
                  day
                }
                @if (bid.period !== 1) {
                  days
                }
              </ng-container>
            </ng-template>
            <p
              class="text-xs font-bold Tagline mb-xxsmall tablet:mb-none mobile:show tablet:hide"
            >
              {{ biddersMultiProfile?.tagline ?? bidder.tagLine }}
            </p>
          </div>
        </div>

        <div class="BidCardBody">
          <div class="DescriptionContainer desktop:mr-small">
            <p
              class="BidDescription mb-xsmall {{
                showShowcases && (hasPortfolioOnBidCardQueryParam$ | flAsync)
                  ? 'desktop:mb-xsmall'
                  : 'desktop:mb-none'
              }}"
              [class.whitespace-pre-line]="displayLineBreaks"
              [flMaxLines]="4"
              [flReadMore]="true"
              (expand)="handleExpand($event)"
            >
              @if (bid.description) {
                {{ bid.description }}
              } @else {
                @if (!showSealedTag) {
                  <ng-container i18n="Bid description">
                    No proposal submitted.
                  </ng-container>
                }
              }
            </p>
            @if (
              showShowcases && (hasPortfolioOnBidCardQueryParam$ | flAsync)
            ) {
              <div
                class="BidCardShowcaseThumbnailContainer mb-xsmall desktop:mb-none"
                [flTrackingSection]="'PortfolioOnBidCard'"
              >
                @for (
                  showcaseItem of bid.showcases;
                  track trackById(i, showcaseItem);
                  let i = $index
                ) {
                  <fl-showcase-thumbnail
                    class="BidCardShowcaseThumbnailContainer-item"
                    matTooltip="View portfolio"
                    i18n-matTooltip="View portfolio tooltip"
                    [flTrackingLabel]="'ShowcaseThumbnailClick'"
                    [flTrackingReferenceId]="showcaseItem.id"
                    [flTrackingReferenceType]="'bid_showcase_item_id'"
                    [matTooltipPosition]="'below'"
                    [matTooltipTouchGestures]="'off'"
                    [showcase]="showcaseItem"
                    (click)="openShowcaseModal(i)"
                  ></fl-showcase-thumbnail>
                }
              </div>
            }
            @if (bidPatternCardActionsNotesComponent) {
              <div class="BidCardFooter">
                <ng-content
                  select="fl-bid-pattern-card-actions-notes"
                ></ng-content>
              </div>
            }
            <div class="BidTagsContainer">
              @if (
                !isEmployerView &&
                !isCollaboratorView &&
                (showBidCalling$ | flAsync) === true &&
                bid.relevanceLabel !== DisplayBidRelevancyLabelApi.C
              ) {
                <div class="TagContainer">
                  <fl-callout
                    [edgeToEdge]="true"
                    [hover]="true"
                    [placement]="CalloutPlacement.RIGHT"
                    [size]="CalloutSize.SMALL"
                    [color]="CalloutColor.DARK"
                    (calloutOpen)="bidCallingVideo.play()"
                  >
                    <fl-callout-trigger>
                      <div
                        class="BidCallingTag mb-xsmall desktop:mb-none mr-xsmall"
                        [class.BidCallingTag--disabled]="!bidderHasBidCalling"
                      >
                        <div class="BidCallingTag-iconContainer">
                          <fl-icon
                            class="BidCallingTag-icon"
                            [color]="IconColor.LIGHT"
                            [name]="'ui-phone'"
                            [size]="IconSize.CHATBOX_HEADER"
                            [useIconFont]="true"
                            [weight]="FontWeight.LIGHT"
                          ></fl-icon>
                        </div>
                        <ng-container i18n="Bid calling tag label">
                          Premier Calling
                        </ng-container>
                      </div>
                    </fl-callout-trigger>

                    <fl-callout-content>
                      <div class="CalloutContent">
                        <fl-video
                          #bidCallingVideo
                          class="CalloutMedia"
                          [autoPlay]="true"
                          [boundedWidth]="true"
                          [disableControls]="true"
                          [isExternal]="false"
                          [loop]="true"
                          [src]="'bids/bid-calling-preview.mp4'"
                        ></fl-video>
                        <p>
                          @if (bidderHasBidCalling && isUsersBid) {
                            <span
                              class="text-light"
                              i18n="Bid calling own bid enabled tooltip"
                            >
                              With Premier membership, clients can call you
                              directly, increasing your chance of winning jobs.
                            </span>
                          } @else if (bidderHasBidCalling && !isUsersBid) {
                            <span
                              class="text-light"
                              i18n="Bid calling other bid enabled tooltip"
                            >
                              With Premier membership, clients can call this
                              freelancer directly, increasing their chance of
                              winning jobs.
                            </span>
                            @if (!userHasBidCalling) {
                              <fl-link
                                i18n="Bid calling benefit link"
                                link="/membership"
                              >
                                Get Premier for yourself.
                              </fl-link>
                            }
                          } @else {
                            <span
                              class="text-light"
                              i18n="Bid calling own bid disabled tooltip"
                            >
                              With Premier membership, clients can call you
                              directly, increasing your chance of winning jobs.
                            </span>
                            <fl-link
                              i18n="Bid calling benefit link"
                              link="/membership"
                            >
                              Get Premier for yourself.
                            </fl-link>
                          }
                        </p>
                      </div>
                    </fl-callout-content>
                  </fl-callout>
                </div>
              }
              @if ((bidCardSettings$ | flAsync)?.bidsSealedPill) {
                <div>
                  @if (showSealedTag) {
                    <div class="TagContainer">
                      @if (isUsersBid) {
                        <fl-upgrade-tag
                          class="mb-xsmall desktop:mb-none mr-xsmall"
                          i18n-matTooltip="Sealed bid description tooltip"
                          matTooltip="Your bid is hidden from other freelancers as it has a sealed upgrade. This prevents others from copying your bid, making it more unique."
                          [matTooltipPosition]="'above'"
                          [upgradeType]="UpgradeType.SEALED"
                        ></fl-upgrade-tag>
                      }
                      @if (!isUsersBid) {
                        <fl-upgrade-tag
                          class="mb-xsmall desktop:mb-none mr-xsmall"
                          i18n-matTooltip="Sealed bid description tooltip"
                          matTooltip="The sealed upgrade hides bids from other freelancers. This prevents others from copying bids, making them more unique."
                          [matTooltipPosition]="'above'"
                          [upgradeType]="UpgradeType.SEALED"
                        ></fl-upgrade-tag>
                      }
                    </div>
                  }
                </div>
              }
              <div class="AdditionalDetailsContainer">
                <ng-content
                  select="fl-bid-pattern-card-additional-details"
                ></ng-content>
              </div>
            </div>
          </div>

          <div class="CtaActionContainer">
            <ng-content select="fl-bid-pattern-card-actions"></ng-content>
          </div>
        </div>

        <div class="AnnotationContainer">
          <ng-content select="fl-bid-pattern-card-annotation"></ng-content>
        </div>
      </div>
    </ng-container>
  `,
  styleUrls: ['./bid-pattern-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BidPatternCardComponent
  implements AfterViewInit, OnInit, OnChanges, OnDestroy
{
  private featureFlagService = inject(FeatureFlagsService);
  private modalService = inject(ModalService);
  private route = inject(ActivatedRoute);

  AvatarSize = AvatarSize;
  CalloutColor = CalloutColor;
  CalloutPlacement = CalloutPlacement;
  CalloutSize = CalloutSize;
  DisplayBidRelevancyLabelApi = DisplayBidRelevancyLabelApi;
  Feature = Feature;
  FontWeight = FontWeight;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkUnderline = LinkUnderline;
  OnlineIndicatorSize = OnlineIndicatorSize;
  RibbonColor = RibbonColor;
  RibbonPlacement = RibbonPlacement;
  RibbonType = RibbonType;
  TagColor = TagColor;
  TagSize = TagSize;
  UpgradeType = UpgradeType;
  UsernameSize = UsernameSize;
  trackById = trackById;

  @HostBinding('attr.data-ribbon-type')
  ribbon: RibbonType | undefined;

  isBidAwardedAndAccepted: boolean;
  isBidder? = true;
  isDeloitteProject: boolean;
  isInsourceProject: boolean;
  isProjectHourly: boolean;
  isProjectOpen: boolean;
  isTokenProject: boolean;
  showBidAmountAndDescription = true;
  showRibbon = true;
  showNewFreelancerLabel = true;
  showMoreOptions: boolean;
  showSealedTag = false;
  showShowcases = false;

  displayLineBreaks = false;
  userBadges: UsernameBadges;
  userLinkQueryParams: QueryParams;

  ratingControl = new FormControl(0, { nonNullable: true });

  @Input() agent?: User;
  @Input() bid: ProjectViewBid;
  @Input() bidder: ProjectViewUser;
  @Input() biddersMultiProfile?: Profile;
  @Input() clientAgreement?: ProjectContractSignatures;
  @Input() employer: ProjectViewUser;
  @Input() hasBidAwardPermission: boolean;
  @Input() bidderHasBidCalling: boolean;
  @Input() userHasBidCalling?: boolean;
  @Input() hasChatPermission: boolean;
  @Input() insourceEnabledPools: readonly PoolModel[] | undefined;
  @Input() ipContract?: IpContract;
  @Input() isCollaboratorView: boolean;
  @Input() isCorporateUser: boolean;
  @Input() isDeloitteExternalBid: boolean;
  @Input() isEmployerView: boolean;
  @Input() isUsersBid: boolean;
  @Input() isOnline: boolean;
  @Input() project: Project;
  @Input() projectFee: BidBuyerProjectFee;
  @Input() rating?: BidRating;
  @Input() reputation?: FreelancerReputation;
  @Input() salesTaxEnabled?: boolean;
  @Input() showBidRelevanceWarning = false;
  @Input() showCity?: boolean;
  @Input() showOnboardingModal?: boolean;
  @Input() talentNetworkPools: readonly PoolModel[] | undefined;
  @Input() userId: number;
  @Input() videoSrc?: string;

  @Output() clientSeeBid = new EventEmitter<Bid>();
  @Output() videoModalVideoPlayed = new EventEmitter<void>();
  @Output() videoModalVideoEnded = new EventEmitter<void>();

  showBidCalling$: Observable<boolean>;
  bidCardSettings$: Observable<
    | {
        readonly bidCardCountry: boolean;
        readonly bidCardEarnings: boolean;
        readonly bidsSealedPill: boolean;
        readonly bidTotalFee: boolean;
        readonly bidCalling: boolean;
      }
    | false
  >;

  freelancerBidRestrictions$: Observable<FreelancerBidRestrictions>;
  priceTooltip?: string;
  totalFee: number;
  showPaymentFeeRate$ = this.featureFlagService.getConfig('bidCard').pipe(
    map(settings => {
      const bidTotalFee =
        settings && typeof settings === 'object' ? settings.bidTotalFee : false;
      return !!bidTotalFee && (this.isEmployerView || this.isCollaboratorView);
    }),
  );

  @ContentChild(BidPatternCardActionsNotesComponent)
  bidPatternCardActionsNotesComponent: BidPatternCardActionsNotesComponent;

  @ViewChildren('bidCard', { read: ElementRef })
  bidCard: QueryList<ElementRef>;

  private _isUsersBidSubject$ = new ReplaySubject<boolean>(1);
  isUsersBid$: Observable<boolean> = this._isUsersBidSubject$.asObservable();

  private _bidderHasBidCallingSubject$ = new ReplaySubject<boolean>(1);
  bidderHasBidCalling$: Observable<boolean> =
    this._bidderHasBidCallingSubject$.asObservable();

  private bidCardSubscription?: Subscription;

  bidSeenObserver: IntersectionObserver;
  multiProfileId: number | undefined;

  hasPortfolioOnBidCardQueryParam$: Observable<boolean>;

  ngOnInit(): void {
    this.bidCardSettings$ = this.featureFlagService.getConfig('bidCard');
    this.showBidCalling$ = combineLatest([
      this.bidCardSettings$,
      this.isUsersBid$,
      this.bidderHasBidCalling$,
    ]).pipe(
      map(([settings, isUsersBid, bidderHasBidCalling]) => {
        const isBidCallingFeatureEnabled = settings && settings.bidCalling;
        if (!isBidCallingFeatureEnabled) {
          return false;
        }

        return bidderHasBidCalling || isUsersBid;
      }),
    );

    this.userBadges = {
      verified: this.bidder.status.freelancerVerifiedUser,
      corporate:
        this.bidder.corporate &&
        this.bidder.corporate.status === CorporateStatusApi.ACTIVE,
      preferred_freelancer: this.bidder.preferredFreelancer,
      shareholder: this.bidder.isShareholder,
      rising_star: this.bidder.isRisingStar,
    };

    const isExternalBidder = this.talentNetworkPools?.some(
      pool =>
        this.project.poolIds?.includes(pool.id) &&
        this.bidder.poolIds?.includes(pool.id),
    );

    const isInternalBidder = this.insourceEnabledPools?.some(
      pool =>
        this.project.poolIds?.includes(pool.id) &&
        this.bidder.poolIds?.includes(pool.id),
    );

    this.showNewFreelancerLabel = !(isExternalBidder || isInternalBidder);

    this.priceTooltip = this.salesTaxEnabled
      ? undefined
      : $localize`Price includes tax`;

    this.userLinkQueryParams = {
      projectId: this.project.id,
      ref_bid_id: this.bid.id,
      ref_project_id: this.project.id,
    };

    this.totalFee = this.bid.amount + (this.projectFee?.amount ?? 0);

    this.hasPortfolioOnBidCardQueryParam$ = this.route.queryParamMap.pipe(
      map(params => params.has('showPortfolioOnBidCard')),
    );
  }

  ngAfterViewInit(): void {
    this.bidCardSubscription = this.bidCard.changes
      .pipe(
        startWith(this.bidCard),
        map(el => el && el.first),
        filter(isDefined),
        take(1),
      )
      .subscribe(el => {
        if (this.isEmployerView) {
          this.bidSeenObserver = new IntersectionObserver(
            ([entry], observer) => {
              if (entry.intersectionRatio === 1) {
                this.clientSeeBid.emit(this.bid);
                observer.unobserve(el.nativeElement);
              }
            },
            {
              rootMargin: '0px',
              threshold: 1.0,
            },
          );
          this.bidSeenObserver.observe(el.nativeElement);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.showSealedTag = false;
    this.showShowcases = this.bid.showcases
      ? this.bid.showcases.length > 0
      : false;
    this.showBidAmountAndDescription = true;
    this.showRibbon = true;
    if (this.bidder) {
      if (!this.isUsersBid && this.bidder.isNewFreelancer) {
        this.isBidder = false;
      }
      this.showSealedTag = this.bid.sealed || false;
      if (this.isEmployerView || this.isCollaboratorView) {
        // if recruiter upgrade is enabled,
        // we only show recommendation for recruiter selected bid
        // which has bid.score === 99
        const recommended = this.project.upgrades.assisted
          ? this.bid.score === RECRUITER_RECOMMENDED_SCORE
          : this.bid.recommended;

        this.ribbon = recommended
          ? RibbonType.RECOMMENDED
          : this.bid.sponsored
            ? RibbonType.SPONSORED
            : undefined;

        this.isBidder = false;
      } else {
        this.ribbon = this.bid.sponsored ? RibbonType.SPONSORED : undefined;
        this.showBidAmountAndDescription = this.showSealedTag
          ? this.isUsersBid
          : true;
        this.showRibbon = this.showBidAmountAndDescription;
      }

      this.ratingControl.setValue(
        (this.bidder.reputation &&
          this.bidder.reputation.entireHistory.overall) ||
          0,
      );
    }

    if (this.bid && 'bid' in changes) {
      this.isBidAwardedAndAccepted =
        this.bid.awardStatus === BidAwardStatusApi.AWARDED;
    }

    if (this.project && 'project' in changes) {
      this.isProjectHourly = this.project.type === ProjectTypeApi.HOURLY;
      this.isProjectOpen = this.project.status === ProjectStatusApi.ACTIVE;
      this.isInsourceProject = this.project.isInsourceProject;
      this.isTokenProject = this.project.isTokenProject;
      this.isDeloitteProject =
        !!this.project.enterpriseIds &&
        this.project.enterpriseIds.includes(Enterprise.DELOITTE_DC);
    }

    if ('isUsersBid' in changes) {
      this._isUsersBidSubject$.next(this.isUsersBid);
    }

    if ('bidderHasBidCalling' in changes) {
      this._bidderHasBidCallingSubject$.next(this.bidderHasBidCalling);
    }

    if ('biddersMultiProfile' in changes) {
      this.multiProfileId = changes.biddersMultiProfile.currentValue?.id;
    }

    this.showMoreOptions =
      !this.isEmployerView &&
      !this.isCollaboratorView &&
      !this.isUsersBid &&
      this.isProjectOpen &&
      !this.showSealedTag;

    this.totalFee = this.bid.amount + (this.projectFee?.amount ?? 0);
  }

  ngOnDestroy(): void {
    if (this.bidSeenObserver) {
      this.bidSeenObserver.disconnect();
    }

    if (this.bidCardSubscription) {
      this.bidCardSubscription.unsubscribe();
    }
  }

  handleExpand(isExpanded: boolean): void {
    this.displayLineBreaks = isExpanded;
  }

  handleVideoModalVideoPlayed(): void {
    this.videoModalVideoPlayed.emit();
  }

  handleVideoModalVideoEnded(): void {
    this.videoModalVideoEnded.emit();
  }

  openShowcaseModal(index: number): void {
    this.modalService.open('ShowcaseViewerModal', {
      inputs: {
        bid: this.bid,
        bidder: this.bidder,
        initialIndex: index,
        hasBidAwardPermission: this.hasBidAwardPermission,
        hasChatPermission: this.hasChatPermission,
        isCollaboratorView: this.isCollaboratorView,
        isEmployerView: this.isEmployerView,
        project: this.project,
        showcaseIds: this.bid.showcases.map(({ showcaseId }) => showcaseId),
      },
      edgeToEdge: true,
      customViewHeader: true,
      mobileFullscreen: true,
      size: ModalSize.XLARGE,
    });
  }
}
